import React from 'react';
import { Button } from '@/components/ui/button';
import { Phone, FileText, CheckCircle2, Shield, Clock, Sparkles } from 'lucide-react';

const HeroSection = () => {
  const scrollToContact = (e) => {
    e.preventDefault();
    const element = document.querySelector('#contact');
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const trustBullets = [
    { icon: Sparkles, text: 'Quality finish every time' },
    { icon: Shield, text: 'Tidy & respectful' },
    { icon: Clock, text: 'Clear pricing, no surprises' },
    { icon: Clock, text: 'Reliable' },
  ];

  return (
    <section 
      id="hero" 
      className="relative pt-20 lg:pt-24 min-h-[90vh] flex items-center bg-slate-50"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <CheckCircle2 className="w-4 h-4" />
              Northampton's Experienced & Trusted Plasterers
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 font-['Manrope'] leading-tight mb-6">
              Plastering & Rendering in{' '}
              <span className="text-gradient-red bg-gradient-to-r from-red-600 to-red-500 bg-clip-text text-transparent">
                Northampton
              </span>
            </h1>
            
            <p className="text-lg text-slate-600 mb-8 max-w-xl leading-relaxed">
              Smooth finishes. Clean, professional service. Reliable turnaround. 
              From single rooms to full renovations, we deliver quality plastering 
              that stands the test of time.
            </p>

            {/* Trust Bullets */}
            <div className="flex flex-wrap gap-4 mb-10">
              {trustBullets.map((bullet, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 text-slate-700"
                  data-testid={`trust-bullet-${index}`}
                >
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                    <bullet.icon className="w-4 h-4 text-red-600" />
                  </div>
                  <span className="font-medium text-sm">{bullet.text}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-red-600 hover:bg-red-700 text-white font-semibold text-base px-8 py-6 rounded-full shadow-[0_4px_14px_0_rgba(196,30,58,0.39)] hover:shadow-lg transition-all duration-200 hover:scale-[1.02]"
                onClick={scrollToContact}
                data-testid="hero-quote-btn"
              >
                <FileText className="w-5 h-5 mr-2" />
                Get a Free Quote
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-slate-300 text-slate-700 hover:border-red-600 hover:text-red-600 font-semibold text-base px-8 py-6 rounded-full transition-all duration-200"
                asChild
                data-testid="hero-call-btn"
              >
                <a href="tel:07766111127">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </a>
              </Button>
            </div>
          </div>

          {/* Right Image - Logo */}
          <div className="relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl h-[400px] lg:h-[500px]">
              <img
                src="https://customer-assets.emergentagent.com/job_ld-plaster-preview/artifacts/slboln93_flex-plastering-northamptonshire.png"
                alt="Flex Plastering Logo"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
            
            {/* Floating Badge */}
            <div className="absolute -bottom-4 -left-4 lg:-left-8 bg-white rounded-xl shadow-lg p-4 border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900 font-['Manrope']">Fully Insured</p>
                  <p className="text-sm text-slate-500">Public Liability</p>
                </div>
              </div>
            </div>

            {/* Decorative Element */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-red-200 rounded-full blur-3xl opacity-60" />
          </div>
        </div>
      </div>

      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-red-50 to-transparent -z-10" />
    </section>
  );
};

export default HeroSection;
